import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { APIError, AppThunkAPIType, unknownError } from "../../app/appTypes";
import { RootState } from "../../app/store";
import { getWithAuth, postWithAuth } from "../../http";

export interface UploadedFilePayload {
    guid: string;
    ownerGuid: string;
    fileName: string;
    contentType: string;
    fileData: string;
}

export interface UploadedFileDto {
    guid: string;
    fileName: string;
    contentType: string;
    fileData?: string;
}

export interface UserAppBugHeader {
    id: number;
    guid: string;
    title: string;
    priority: string;
    isBusinessCritical: boolean;
    createDate: string;
    status?: string | null;
}

export interface UserAppBugDetails extends UserAppBugHeader {
    description: string;
    screenLink?: string | null;
    supportComment: string | null;
    uploadedFiles: UploadedFileDto[];
}

export interface CompanyBugHeader {
    id: number;
    guid: string;
    title: string;
    priority: string;
    isBusinessCritical: boolean;
    status: string;
    createDate: string;
    reportedBy?: string | null;
}

export interface CompanyBugDetails extends CompanyBugHeader {
    description: string;
    screenLink?: string | null;
    supportComment?: string | null;
    uploadedFiles: UploadedFileDto[];
}

export interface CreateUserAppBugPayload {
    title: string;
    priority: "H" | "M";
    isBusinessCritical: boolean;
    description: string;
    screenLink?: string;
    diagnosticData: string;
    uploadedFiles?: UploadedFilePayload[];
}

interface SupportIntegrationState {
    bugs: UserAppBugHeader[];
    loading: boolean;
    submitting: boolean;
    error: APIError | null;
    myBugDetails: UserAppBugDetails | null;
    myBugDetailsLoading: boolean;
    myBugDetailsError: APIError | null;
    companyBugs: CompanyBugHeader[];
    companyBugsLoading: boolean;
    companyBugsError: APIError | null;
    companyBugDetails: CompanyBugDetails | null;
    companyBugDetailsLoading: boolean;
    companyBugDetailsError: APIError | null;
}

const initialState: SupportIntegrationState = {
    bugs: [],
    loading: false,
    submitting: false,
    error: null,
    myBugDetails: null,
    myBugDetailsLoading: false,
    myBugDetailsError: null,
    companyBugs: [],
    companyBugsLoading: false,
    companyBugsError: null,
    companyBugDetails: null,
    companyBugDetailsLoading: false,
    companyBugDetailsError: null,
};

export const getMyBugs = createAsyncThunk<UserAppBugHeader[], void, AppThunkAPIType>(
    "supportIntegration/getMyBugs",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: "api/support-integration/bugs" }));

        if (getWithAuth.fulfilled.match(result)) {
            return result.payload;
        }

        return rejectWithValue(result.payload ?? unknownError());
    }
);

export const getMyBugDetails = createAsyncThunk<UserAppBugDetails, number, AppThunkAPIType>(
    "supportIntegration/getMyBugDetails",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/support-integration/bugs/${id}` }));

        if (getWithAuth.fulfilled.match(result)) {
            return result.payload;
        }

        return rejectWithValue(result.payload ?? unknownError());
    }
);

export const getCompanyBugs = createAsyncThunk<CompanyBugHeader[], void, AppThunkAPIType>(
    "supportIntegration/getCompanyBugs",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: "api/support-integration/company-bugs" }));

        if (getWithAuth.fulfilled.match(result)) {
            return result.payload;
        }

        return rejectWithValue(result.payload ?? unknownError());
    }
);

export const getCompanyBugDetails = createAsyncThunk<CompanyBugDetails, number, AppThunkAPIType>(
    "supportIntegration/getCompanyBugDetails",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/support-integration/company-bugs/${id}` }));

        if (getWithAuth.fulfilled.match(result)) {
            return result.payload;
        }

        return rejectWithValue(result.payload ?? unknownError());
    }
);

export const addBug = createAsyncThunk<UserAppBugHeader, CreateUserAppBugPayload, AppThunkAPIType>(
    "supportIntegration/addBug",
    async (payload, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: "api/support-integration/bugs",
            payload,
        }));

        if (postWithAuth.fulfilled.match(result)) {
            return result.payload;
        }

        return rejectWithValue(result.payload ?? unknownError());
    }
);

const supportIntegrationSlice = createSlice({
    name: "supportIntegration",
    initialState,
    reducers: {
        clearSupportError: (state) => {
            state.error = null;
        },
        clearCompanyBugDetails: (state) => {
            state.companyBugDetails = null;
            state.companyBugDetailsError = null;
        },
        clearMyBugDetails: (state) => {
            state.myBugDetails = null;
            state.myBugDetailsError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getMyBugs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getMyBugs.fulfilled, (state, action) => {
                state.loading = false;
                state.bugs = action.payload;
            })
            .addCase(getMyBugs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? unknownError();
            })
            .addCase(getCompanyBugs.pending, (state) => {
                state.companyBugsLoading = true;
                state.companyBugsError = null;
            })
            .addCase(getCompanyBugs.fulfilled, (state, action) => {
                state.companyBugsLoading = false;
                state.companyBugs = action.payload;
            })
            .addCase(getCompanyBugs.rejected, (state, action) => {
                state.companyBugsLoading = false;
                state.companyBugsError = action.payload ?? unknownError();
            })
            .addCase(getCompanyBugDetails.pending, (state) => {
                state.companyBugDetails = null;
                state.companyBugDetailsLoading = true;
                state.companyBugDetailsError = null;
            })
            .addCase(getCompanyBugDetails.fulfilled, (state, action) => {
                state.companyBugDetailsLoading = false;
                state.companyBugDetails = action.payload;
            })
            .addCase(getCompanyBugDetails.rejected, (state, action) => {
                state.companyBugDetailsLoading = false;
                state.companyBugDetailsError = action.payload ?? unknownError();
            })
            .addCase(getMyBugDetails.pending, (state) => {
                state.myBugDetails = null;
                state.myBugDetailsLoading = true;
                state.myBugDetailsError = null;
            })
            .addCase(getMyBugDetails.fulfilled, (state, action) => {
                state.myBugDetailsLoading = false;
                state.myBugDetails = action.payload;
            })
            .addCase(getMyBugDetails.rejected, (state, action) => {
                state.myBugDetailsLoading = false;
                state.myBugDetailsError = action.payload ?? unknownError();
            })
            .addCase(addBug.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(addBug.fulfilled, (state, action) => {
                state.submitting = false;
                state.bugs.unshift(action.payload);
            })
            .addCase(addBug.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload ?? unknownError();
            });
    },
});

export const { clearSupportError, clearCompanyBugDetails, clearMyBugDetails } = supportIntegrationSlice.actions;

export const selectSupportIntegration = (state: RootState) => state.supportIntegration;

export default supportIntegrationSlice.reducer;